import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { ChangeStepper } from "@/payroll/editor/change-stepper";
import { PaneSearchControl } from "@/payroll/editor/pane-search-control";
import type { PaneSearch } from "@/payroll/editor/use-pane-search";
import { markCount, markRing, type CellMark } from "./comparison-grid";

/**
 * One status line: how much of the payroll moved, then how this one sheet
 * moved, then the way through it. Each kind of change names itself next to the
 * swatch that marks it.
 */
export const ChangeSummary = ({
  search,
  changedSheets,
  sheets,
  changes,
  counts,
  goToChange,
  changedOnly,
  onChangedOnlyChange,
}: {
  search: PaneSearch;
  changedSheets: unknown[];
  sheets: unknown[];
  /** One entry per changed cell, in reading order. */
  changes: unknown[];
  counts: Record<CellMark, number>;
  goToChange: (step: number) => void;
  changedOnly: boolean;
  onChangedOnlyChange: (value: boolean) => void;
}) => (
  // Chrome, like the strip above it: how much moved, and the way through it.
  <Stack
    gap={0}
    align="stretch"
    direction="row"
    className="flex min-h-10 flex-wrap items-center gap-x-3 gap-y-1 bg-surface-sunken px-3 py-1 text-xs"
  >
    {!!changes.length && (
      <ChangeStepper
        noun="change"
        label={`${changes.length} ${changes.length === 1 ? "change" : "changes"}`}
        onStep={goToChange}
      />
    )}
    <span className="text-subtle">
      {changedSheets.length} of {sheets.length}{" "}
      {sheets.length === 1 ? "sheet" : "sheets"} changed
    </span>
    {!!changedSheets.length && changedSheets.length < sheets.length && (
      <Button
        size="xs"
        variant="link"
        className="h-auto min-w-0 p-0 font-medium"
        onClick={() => onChangedOnlyChange(!changedOnly)}
      >
        {changedOnly
          ? `Show all ${sheets.length}`
          : "Only the ones that changed"}
      </Button>
    )}
    {!!changes.length && (
      <span aria-hidden className="text-line">
        |
      </span>
    )}
    {!!changes.length && (
      <>
        {(Object.keys(counts) as CellMark[])
          .filter((mark) => counts[mark] > 0)
          .map((mark) => (
            <span key={mark} className="flex items-center gap-1.5 text-subtle">
              <span
                aria-hidden
                style={markRing(mark)}
                className="h-[13px] w-[13px] rounded-[3px] bg-surface-raised"
              />
              {markCount(mark, counts[mark])}
            </span>
          ))}
      </>
    )}
    <PaneSearchControl search={search} label="sheet" className="ml-auto" />
  </Stack>
);
